import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ComposedChart,
  Legend,
} from 'recharts';
import {
  SleepSession,
  SleepStageType,
  STAGE_COLORS,
  STAGE_LABELS,
} from '../services/sleepService';

interface SleepChartProps {
  sessions: SleepSession[];
  loading?: boolean;
  error?: string | null;
  selectedDate?: string | null;
  onSelectDate?: (date: string) => void;
}

const STAGE_ORDER: SleepStageType[] = ['DEEP', 'REM', 'LIGHT', 'AWAKE', 'UNSPECIFIED'];

interface DayBucket {
  date: string;
  label: string;
  DEEP: number;
  REM: number;
  LIGHT: number;
  AWAKE: number;
  UNSPECIFIED: number;
  totalHours: number;
  efficiency: number | null;
  sessionCount: number;
  napCount: number;
}

const formatDayLabel = (iso: string) => {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}`;
};

const aggregateByDay = (sessions: SleepSession[]): DayBucket[] => {
  const map = new Map<string, DayBucket>();

  for (const session of sessions) {
    const date = session.session_date;
    let bucket = map.get(date);
    if (!bucket) {
      bucket = {
        date,
        label: formatDayLabel(date),
        DEEP: 0,
        REM: 0,
        LIGHT: 0,
        AWAKE: 0,
        UNSPECIFIED: 0,
        totalHours: 0,
        efficiency: null,
        sessionCount: 0,
        napCount: 0,
      };
      map.set(date, bucket);
    }

    bucket.sessionCount += 1;
    if (session.kind === 'NAP') bucket.napCount += 1;

    let hasStages = false;
    for (const stage of session.stages || []) {
      hasStages = true;
      const hours = (stage.duration_minutes || 0) / 60;
      bucket[stage.stage_type] = (bucket[stage.stage_type] || 0) + hours;
    }

    if (!hasStages) {
      const start = new Date(session.start_time).getTime();
      const end = new Date(session.end_time).getTime();
      const hours = Math.max(0, (end - start) / (1000 * 60 * 60));
      bucket.UNSPECIFIED += hours;
    }

    if (session.kind === 'MAIN' && typeof session.efficiency === 'number') {
      bucket.efficiency = session.efficiency;
    }
  }

  const buckets = Array.from(map.values());
  buckets.forEach((bucket) => {
    bucket.totalHours =
      bucket.DEEP + bucket.REM + bucket.LIGHT + bucket.AWAKE + bucket.UNSPECIFIED;
    STAGE_ORDER.forEach((s) => {
      bucket[s] = Math.round(bucket[s] * 100) / 100;
    });
    bucket.totalHours = Math.round(bucket.totalHours * 100) / 100;
  });

  return buckets.sort((a, b) => a.date.localeCompare(b.date));
};

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const data: DayBucket | undefined = payload[0]?.payload;
  if (!data) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        padding: 10,
        fontSize: 12,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ marginBottom: 4 }}>Total: <strong>{data.totalHours.toFixed(1)}h</strong></div>
      {STAGE_ORDER.map((s) =>
        data[s] > 0 ? (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                background: STAGE_COLORS[s],
                borderRadius: 2,
              }}
            />
            <span>{STAGE_LABELS[s]}: {data[s].toFixed(1)}h</span>
          </div>
        ) : null
      )}
      {data.efficiency !== null && (
        <div style={{ marginTop: 6, color: '#0ea5e9' }}>Eficiencia: {data.efficiency.toFixed(1)}%</div>
      )}
      <div style={{ marginTop: 6, color: '#6b7280' }}>
        {data.sessionCount} sesión(es){data.napCount > 0 ? ` · ${data.napCount} siesta(s)` : ''}
      </div>
    </div>
  );
};

const SleepChart: React.FC<SleepChartProps> = ({
  sessions,
  loading = false,
  error = null,
  selectedDate,
  onSelectDate,
}) => {
  const chartData = useMemo(() => aggregateByDay(sessions), [sessions]);

  if (loading) {
    return (
      <div style={{ width: '100%', height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>Cargando datos de sueño...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ width: '100%', height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#e53e3e', fontSize: 14 }}>{error}</div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div style={{ width: '100%', height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#666', fontSize: 14 }}>No hay datos de sueño en este rango</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartData}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis
            yAxisId="left"
            domain={[0, 12]}
            ticks={[0, 3, 6, 9, 12]}
            tickFormatter={(v) => `${v}h`}
            label={{ value: 'Horas', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) => {
              if (value === 'efficiency') return 'Eficiencia';
              return STAGE_LABELS[value as SleepStageType] || value;
            }}
          />
          {STAGE_ORDER.map((stage) => (
            <Bar
              key={stage}
              yAxisId="left"
              dataKey={stage}
              stackId="sleep"
              fill={STAGE_COLORS[stage]}
              cursor="pointer"
              onClick={(data: any) => {
                const date = data?.date ?? data?.payload?.date;
                if (date && onSelectDate) onSelectDate(date);
              }}
              shape={(props: any) => {
                const isSelected = props.payload.date === selectedDate;
                return (
                  <rect
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    fill={props.fill}
                    stroke={isSelected ? '#111827' : 'none'}
                    strokeWidth={isSelected ? 2 : 0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (onSelectDate) onSelectDate(props.payload.date);
                    }}
                  />
                );
              }}
            />
          ))}
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="efficiency"
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={{ r: 3, fill: '#0ea5e9' }}
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SleepChart;
